import { useUserEventLogger } from '@repo/tracking-service';
import { Button } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { YesNoBlankAddressUnknown } from '@/__generated__/businessLocationsMutation.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { WithButtonList, WithChevronList } from '@/components/survey/common/ui';
import { ProgressBar } from '@/components/survey/common/ui/ProgressBar';
import listStyle from '@/components/survey/common/ui/styles/WithButtonList.module.scss';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertLocations } from '@/graphql/mutation/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';
import { locationFormAtom, locationsDataAtom, readonlyLocationsForServerAtom, someIsFirstLocationAtom } from '@/lib/stores/survey/business';
import Arrow from '@/public/images/survey/ic-listin-right.svg';

export const AllLocationsContainer = () => {
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const router = useRouter();
  const [upsertAnswer, isLoading] = useUpsertBusinessAnswer(upsertLocations);
  const locations = useAtomValue(locationsDataAtom).locations;
  const serverSchemeLocations = useAtomValue(readonlyLocationsForServerAtom);
  const someIsFirstLocation = useAtomValue(someIsFirstLocationAtom);
  const setEditLocationInfo = useSetAtom(locationFormAtom);

  const sortLocations = useMemo(
    () =>
      locations
        // 원본 index 보존을 위해 복사
        .slice()
        .map((item, idx) => ({ idx, ...item }))
        .sort((a, b) => {
          // 1. isHometaxRegAddress가 true인 항목을 최상단에 배치
          if (a.isHometaxRegAddress && !b.isHometaxRegAddress) return -1;
          if (!a.isHometaxRegAddress && b.isHometaxRegAddress) return 1;

          // 2. isFirstLocation이 'yes'인 항목을 최하단에 배치
          if (a.isFirstLocation === 'yes' && b.isFirstLocation !== 'yes') return 1;
          if (a.isFirstLocation !== 'yes' && b.isFirstLocation === 'yes') return -1;

          // 3. 나머지는 연도 내림차순(최신순)으로 정렬
          return (b.year || 0) - (a.year || 0);
        }),
    [locations]
  );

  const goAddLocationPage = () => {
    router.push(SURVEY_PAGES.주소이전_추가수정);
  };
  const handleEditLocation = (index: number) => {
    const location = locations[index];
    setEditLocationInfo({
      index,
      isFirstLocation: location?.isFirstLocation || 'blank',
      year: location?.year || 0,
      city: location?.address.split(' ')[0] || '',
      address1: location?.address.split(' ')[1],
      address2: location?.address.split(' ')[2]
    });
    goAddLocationPage();
  };

  const handleSubmit = (isAddressUnknown?: YesNoBlankAddressUnknown) => {
    upsertAnswer(
      {
        isCompleted: true,
        isFirstLocation: isAddressUnknown || 'no',
        locations: serverSchemeLocations
      },
      {
        onCompleted: () => {
          router.push(SURVEY_PAGES.사업장별질문);
        }
      }
    );
  };

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_relocation-address-register_back'} />
      <main className={cn(common.main_layout, 'pb-[140px]')}>
        <ProgressBar step={5} />
        <article>
          <section>
            <h1 className={common.content_title}>
              최초 등록한 사업장 주소지와
              <br />
              이전한 주소지를 등록해주세요
              <span className={'text-16 mt-3 block font-normal leading-[23px] text-gray-700'}>
                정확한 환급액 계산을 위해서 최초 사업장 주소지와 그후 거쳐온 사업장의 주소지를 등록해주세요.
              </span>
            </h1>

            {someIsFirstLocation && (
              <WithButtonList
                button={{
                  label: '추가',
                  onClick: () => {
                    sendClickEvent('survey_relocation-address-register_add-other-location', eventAttributes);

                    goAddLocationPage();
                  }
                }}
                className={'my-4'}
              >
                <p className={cn(listStyle.with_button_list_title)}>이전 사업장 주소지 추가하기</p>
              </WithButtonList>
            )}

            <div className={'pt-6'}>
              <h2 className={common.section_title}>추가한 사업장 이전 연도와 주소예요</h2>
              <ul>
                {sortLocations.map(({ idx, ...location }) => (
                  <li key={idx}>
                    <WithChevronList
                      onClick={() => {
                        sendClickEvent('survey_relocation-address-register_info', eventAttributes);

                        if (location.isHometaxRegAddress) {
                          router.push(SURVEY_PAGES.주소이전_현재주소_이전연도);
                          return;
                        }
                        handleEditLocation(idx);
                      }}
                    >
                      <p className={'body-13 mb-1 text-gray-700'}>
                        {!location?.year
                          ? '이전 연도 모름'
                          : location.isFirstLocation === 'yes'
                            ? `${location?.year}년도 사업장 등록`
                            : `${location?.year}년도 이전함`}
                      </p>
                      <p>
                        <span className={'headline-16 align-middle text-gray-800'}>{location.address}</span>
                        {location.isFirstLocation === 'yes' && <BadgeIsFirstLocation />}
                      </p>
                    </WithChevronList>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <Button.StickyBottom.Wrapper className={'px-4.5'}>
            <div className={'flex w-full flex-col items-center gap-3'}>
              <Button.StickyBottom.Button
                disabled={isLoading}
                type={'button'}
                state={'primary'}
                onClick={() => {
                  if (someIsFirstLocation) {
                    sendClickEvent('survey_relocation-address-register_confirm', eventAttributes);

                    handleSubmit();
                  } else {
                    sendClickEvent('survey_relocation-address-register_add-first-location', eventAttributes);

                    setEditLocationInfo((prev) => ({
                      ...prev,
                      isFirstLocation: 'yes'
                    }));
                    goAddLocationPage();
                  }
                }}
              >
                {someIsFirstLocation ? '이전한 주소지 등록 완료' : '최초 사업장 주소지 등록'}
              </Button.StickyBottom.Button>
              <button
                type={'button'}
                disabled={isLoading}
                className={'flex items-center'}
                onClick={() => {
                  sendClickEvent('survey_relocation-address-register_confirm-unknown', eventAttributes);

                  handleSubmit(someIsFirstLocation ? 'no' : 'addressUnknown');
                }}
              >
                <span className={'text-15 font-normal leading-5 text-gray-700'}>
                  {someIsFirstLocation ? '나머지 이전한 연도·주소지는 잘 모르겠어요' : '주소를 잘 모르겠어요'}
                </span>
                <span className={'flex h-4 w-3 items-center justify-center text-gray-400'}>
                  <Arrow width={12} height={16} />
                </span>
              </button>
            </div>
          </Button.StickyBottom.Wrapper>
        </article>
      </main>
    </>
  );
};

const BadgeIsFirstLocation = () => {
  return <span className={'caption-12-medium ml-1 inline-block rounded-3xl bg-violet-100 px-1.5 py-0.5 text-violet-600'}>최초 사업장</span>;
};
